import { useSyncExternalStore } from 'react'

/**
 * URL routing, small on purpose. Every page has a real URL so links can be
 * shared. Depth gives detail pages their direction; sidebar order gives
 * peer pages theirs. The detail routes (a job's page, a client's page) join
 * as their phases build them.
 */
/** The People page's three rooms, each a page of its own under /people. */
export type PeopleSection = 'requests' | 'invites' | 'everyone'
export const PEOPLE_SECTIONS: readonly PeopleSection[] = ['requests', 'invites', 'everyone']

export type Route =
  | { name: 'home' }
  | { name: 'jobs' }
  | { name: 'job'; id: string }
  | { name: 'payment-sheet'; id: string }
  | { name: 'clients' }
  | { name: 'client'; id: string }
  | { name: 'people'; section?: PeopleSection }
  | { name: 'activity' }
  // The player's destinations, in the same shell.
  | { name: 'work' }
  | { name: 'payments' }
  | { name: 'announcements'; id?: string }
  | { name: 'settings'; section: string }
  | { name: 'privacy' }
  | { name: 'terms' }
  | { name: 'cookies' }
  | { name: 'not-found' }

export function parse(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent)
  if (parts.length === 0) return { name: 'home' }
  if (parts[0] === 'jobs' && parts.length === 1) return { name: 'jobs' }
  if (parts[0] === 'jobs' && parts[1] && parts.length === 2) return { name: 'job', id: parts[1] }
  if (parts[0] === 'clients' && parts.length === 1) return { name: 'clients' }
  if (parts[0] === 'clients' && parts[1] && parts.length === 2) return { name: 'client', id: parts[1] }
  if (parts[0] === 'people' && parts.length === 1) return { name: 'people' }
  if (parts[0] === 'people' && parts.length === 2 && PEOPLE_SECTIONS.includes(parts[1] as PeopleSection)) {
    return { name: 'people', section: parts[1] as PeopleSection }
  }
  if (parts[0] === 'activity' && parts.length === 1) return { name: 'activity' }
  // The portal's old address lands on the player's Home.
  if (parts[0] === 'portal' && parts.length === 1) return { name: 'home' }
  if (parts[0] === 'work' && parts.length === 1) return { name: 'work' }
  if (parts[0] === 'payments' && parts.length === 1) return { name: 'payments' }
  if (parts[0] === 'payments' && parts[1] && parts.length === 2) return { name: 'payment-sheet', id: parts[1] }
  if (parts[0] === 'announcements' && parts.length === 1) return { name: 'announcements' }
  if (parts[0] === 'announcements' && parts[1] && parts.length === 2) return { name: 'announcements', id: parts[1] }
  if (parts[0] === 'settings' && parts.length <= 2) return { name: 'settings', section: parts[1] ?? 'account' }
  // The internal legal pages, readable without signing in.
  if (parts[0] === 'privacy' && parts.length === 1) return { name: 'privacy' }
  if (parts[0] === 'terms' && parts.length === 1) return { name: 'terms' }
  if (parts[0] === 'cookies' && parts.length === 1) return { name: 'cookies' }
  return { name: 'not-found' }
}

export function href(route: Route): string {
  switch (route.name) {
    case 'home':
      return '/'
    case 'jobs':
      return '/jobs'
    case 'job':
      return `/jobs/${encodeURIComponent(route.id)}`
    case 'clients':
      return '/clients'
    case 'client':
      return `/clients/${encodeURIComponent(route.id)}`
    case 'people':
      return route.section ? `/people/${route.section}` : '/people'
    case 'activity':
      return '/activity'
    case 'work':
      return '/work'
    case 'payments':
      return '/payments'
    case 'payment-sheet':
      return `/payments/${encodeURIComponent(route.id)}`
    case 'announcements':
      return route.id ? `/announcements/${encodeURIComponent(route.id)}` : '/announcements'
    case 'settings':
      return `/settings/${route.section}`
    case 'privacy':
      return '/privacy'
    case 'terms':
      return '/terms'
    case 'cookies':
      return '/cookies'
    case 'not-found':
      return '/'
  }
}

interface Location {
  route: Route
  key: string
}

function read(): Location {
  const route = parse(window.location.pathname)
  // A tree page (settings) is one surface: its section changes do not remount it.
  const key = route.name === 'settings' ? route.name : window.location.pathname
  return { route, key }
}

let current = read()
const listeners = new Set<() => void>()

function update() {
  current = read()
  listeners.forEach((listener) => listener())
}

window.addEventListener('popstate', update)

export function navigate(to: Route | string, options: { replace?: boolean } = {}) {
  const url = typeof to === 'string' ? to : href(to)
  if (url === window.location.pathname + window.location.search) return
  if (options.replace) window.history.replaceState(null, '', url)
  else window.history.pushState(null, '', url)
  update()
}

export function useLocation(): Location {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
  )
}
